/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { RefObject, ComponentChildren } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import { CurrentKeyDetail } from '../UNSAFE_Collection';
import { Floating } from '../UNSAFE_Floating';
import { Layer } from '../UNSAFE_Layer';
import { useOutsideClick } from '../hooks/UNSAFE_useOutsideClick';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { cssProps } from '@oracle/oraclejet-cssprops';

import { MenuContext } from './MenuContext';
import { useMenuCurrentKey } from './useMenuCurrentKey';
import {
  getPrevNextKey,
  getFirstVisibleKey,
  keyExtractor,
  getLastVisibleKey,
  getKeyboardCloseProps,
  getMenuPointerDown
} from './menuUtils';

type MenuProps = {
  /**
   * Trigger element reference
   */
  anchorRef: RefObject<HTMLElement>;
  /**
   * Set of menu groups or menu items that menu will hold.Even though you could pass other type of children,
   * is strongly recommended to use MenuItem, menu groups or components that return this type of components
   * since passing other type of components children produces not desirable behaviors.
   */
  children: ComponentChildren;
  /**
   * Specifies if menu is opened.
   */
  isOpen?: boolean;
  /**
   * Property that triggers a callback when menu is supposed to be closed.
   */
  onClose?: (detail: CloseDetail) => void;
  /**
   * Specifies some screen reader text. Set it to create an accesible menu.
   */
  accessibleLabel?: string;
  /**
   *  Determines focus behavior when the menu is opened.
   */
  initialFocus?: 'menu' | 'firstItem';
};

type CloseDetail =
  | {
      reason: 'cancel' | 'tabbing' | 'itemAction';
    }
  | {
      reason: 'outsideClick';
      target: Element;
    };

const styles = {
  // TODO: Handle vertical and horizontal overflow. Get rid of maxWidth on a next drop so horizontal and also vertical flow is handled correctly.
  // It's hard to relay just in css, we might need to relay on some kind of observer. Maybe should be handled on Floating/Dropdown component.
  floatingContainer: cssProps`${{
    maxWidth: '90vw',
    width: 'max-content',
    borderRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-border-radius-lg)',
    boxShadow: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-dropdown-box-shadow)',
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-bg-color-content)'
  }}`,
  menuContainer: cssProps`${{
    outlineStyle: 'none',
    // paddingTop and Bottom here so menuConatiner have same size as floatingContainer so click event has
    // same clickable area and we can handle focus behavior
    paddingTop: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)',
    paddingBottom: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)'
  }}`
};

export const Menu = ({
  children,
  isOpen = false,
  anchorRef,
  onClose,
  accessibleLabel,
  initialFocus = 'menu'
}: MenuProps) => {
  const floatingRef = useRef<HTMLElement | null>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);
  const isOpenPrevValue = useRef<boolean>();

  const [currentKey, setCurrentKey] = useState<string | undefined>();

  const [possibleFocus, setPossibleFocus] = useState<'pending' | 'menu' | 'firstItem'>('pending');

  const changeKey = useCallback((detail: CurrentKeyDetail<string>) => {
    setCurrentKey(detail.value);
    setPossibleFocus('firstItem');
  }, []);

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(
    (elem: Element | null) => {
      return !!menuContainerRef.current?.contains(elem);
    },
    ['ArrowUp', 'ArrowDown'],
    (elem: Element) => {
      const nextKey = keyExtractor(elem as HTMLElement);
      return !!nextKey;
    }
  );

  const { currentKeyProps } = useMenuCurrentKey(
    (element) => keyExtractor(element),
    getPrevNextKey(menuContainerRef.current, true, currentKey),
    getPrevNextKey(menuContainerRef.current, false, currentKey),
    () => getFirstVisibleKey(menuContainerRef.current),
    () => getLastVisibleKey(menuContainerRef.current),
    currentKey,
    changeKey
  );

  const useOutsideClickHandler = useCallback(
    (e: MouseEvent) => {
      onClose?.({ reason: 'outsideClick', target: e.target as Element });
    },
    [onClose]
  );

  useOutsideClick({
    isDisabled: !isOpen,
    ref: [floatingRef],
    handler: useOutsideClickHandler
  });

  useEffect(() => {
    if (isOpen === isOpenPrevValue.current) return;

    if (isOpen) {
      const menuContainer = menuContainerRef.current;
      if (menuContainer) {
        if (initialFocus === 'menu') {
          // At this point floating has visibility set as hidden, so also menuContainer. Settimeout added so menuContainer
          // is visible at that point and can be focused
          setTimeout(() => {
            menuContainer.focus();
          }, 0);
          setPossibleFocus('menu');
        } else {
          const firstItemKey = getFirstVisibleKey(menuContainerRef.current);
          if (firstItemKey) {
            changeKey({ value: firstItemKey });
          } else {
            setPossibleFocus('menu');
          }
        }
      }
    } else {
      setPossibleFocus('pending');
      setCurrentKey(undefined);
    }

    isOpenPrevValue.current = isOpen;
  }, [isOpen, initialFocus, changeKey]);

  // TODO - Reuse a dropdown component instead of creating floating by ourselves.
  return !isOpen ? null : (
    <Layer logicalParentRef={anchorRef}>
      <Floating
        class={styles.floatingContainer}
        ref={floatingRef}
        anchorRef={anchorRef}
        placement="bottom-start"
        offsetValue={{
          mainAxis: 4, //TODO: stable does not have a 4px gap, so we'll need to figure out how to make this themable.
          crossAxis: 0
        }}>
        <MenuContext.Provider
          value={{
            onClose,
            currentKey,
            showFocusRing
          }}>
          <div
            style={{
              minWidth: `${
                anchorRef.current && anchorRef.current.offsetWidth > MENU_MIN_WIDTH
                  ? anchorRef.current.offsetWidth
                  : MENU_MIN_WIDTH
              }px`
            }}
            ref={menuContainerRef}
            tabIndex={possibleFocus === 'firstItem' ? -1 : 0}
            role="menu"
            aria-label={accessibleLabel}
            class={styles.menuContainer}
            {...mergeProps(
              focusRingProps,
              currentKeyProps,
              getMenuPointerDown(),
              getKeyboardCloseProps(onClose)
            )}>
            {children}
          </div>
        </MenuContext.Provider>
      </Floating>
    </Layer>
  );
};

const MENU_MIN_WIDTH = 200;
